import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/layout/SiteChrome";
import { Calendar } from "../components/schedule/Calendar";
import { StepIndicator } from "../components/schedule/StepIndicator";
import { TimeSlotPicker, TIME_SLOTS } from "../components/schedule/TimeSlotPicker";
import { FormField, TextInput, TextareaInput, CitySelect, labelSt } from "../components/ui/FormFields";
import { useSell, sellStore, type PickupDetails } from "../store/sellStore";
import { formatNice } from "../utils/dateUtils";
import { submitOrder, getToken, request, submitLead } from "../services/api";

type FKey = keyof PickupDetails;
type Errors = Partial<Record<FKey, string>>;

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 20, padding: "24px 22px", boxShadow: "var(--shadow-md)", ...style }}>{children}</div>;
}

function SectionHead({ icon, bg, title, sub }: { icon: string; bg: string; title: string; sub: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22, paddingBottom: 18, borderBottom: "1px solid var(--border)" }}>
      <div style={{ width: 44, height: 44, borderRadius: 13, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div>
        <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, color: "var(--text)" }}>{title}</h2>
        <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{sub}</p>
      </div>
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel = "Continue →", isSubmit = false, submitting = false }: { onBack: () => void; onNext: () => void; nextLabel?: string; isSubmit?: boolean; submitting?: boolean; }) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <button type="button" onClick={onBack} style={{
        flex: 1, borderRadius: 14, border: "1.5px solid var(--border)", background: "var(--surface)",
        padding: "14px", fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 600,
        color: "var(--text)", cursor: "pointer", transition: "all 0.2s",
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--brand)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; }}>
        ← Back
      </button>
      <button type="button" onClick={onNext} disabled={submitting}
        style={{
          flex: 2, borderRadius: 14, border: "none",
          background: submitting ? "var(--surface2)" : isSubmit ? "linear-gradient(135deg, var(--brand), var(--brand-dark))" : "linear-gradient(135deg, var(--indigo), #4338ca)",
          padding: "14px", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15,
          color: submitting ? "var(--text3)" : "#fff", cursor: submitting ? "not-allowed" : "pointer",
          transition: "all 0.2s",
          boxShadow: submitting ? "none" : isSubmit ? "0 6px 20px rgba(255,107,53,0.35)" : "0 6px 20px rgba(79,70,229,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
        {submitting ? (
          <>
            <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.2)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.75s linear infinite", display: "inline-block" }} />
            Confirming…
          </>
        ) : nextLabel}
      </button>
    </div>
  );
}

export default function SchedulePage() {
  const sell    = useSell();
  const nav     = useNavigate();
  const topRef  = useRef<HTMLDivElement>(null);
  const [step,   setStep]   = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSub] = useState(false);
  const [apiError, setApiError] = useState("");
  const [form,   setForm]   = useState<PickupDetails>({ date: "", timeSlot: "", name: "", address: "", landmark: "", city: "", pincode: "" });

  useEffect(() => { if (sell.finalPrice === undefined || sell.finalPrice === null) nav("/"); }, [sell.finalPrice, nav]);

  const setF = (k: FKey, v: string) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: "" })); };
  const scrollTop = () => setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

  const validate = (s: number): boolean => {
    const e: Errors = {};
    if (s === 0) { if (!form.date) e.date = "Please select a pickup date"; if (!form.timeSlot) e.timeSlot = "Please choose a time slot"; }
    if (s === 1) { if (!form.name?.trim()) e.name = "Enter your full name"; }
    if (s === 2) { if (!form.address?.trim()) e.address = "Enter your pickup address"; if (!form.city) e.city = "Select your city"; if (!/^\d{6}$/.test(form.pincode ?? "")) e.pincode = "Enter a valid 6-digit pincode"; }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => { if (validate(step)) { setStep(s => s + 1); scrollTop(); } };
  const goPrev = () => { setStep(s => s - 1); scrollTop(); };

  const handleSubmit = async () => {
    if (!validate(2)) return;
    setSub(true);
    setApiError("");
    try {
      const model = sell.product;
      const deviceName = model?.name || "Unknown Device";

      let orderId: string | undefined;

      if (getToken() && sell.phone) {
        try {
          const res = await submitOrder({
            productId:       sell.productId,
            deviceName,
            algoId:          sell.algoId,
            basePrice:       sell.algoBasePrice || model?.basePrice || sell.finalPrice!,
            customerName:    form.name!,
            customerPhone:   sell.phone,
            customerAddress: [form.address, form.landmark, form.city, form.pincode].filter(Boolean).join(", "),
            customerLandmark: form.landmark,
            scheduledDate:   form.date!,
            scheduledTime:   form.timeSlot!,
            customerAnswers: sell.answerIndices || {},
            customerCondition: sell.conditionSummary || [],
            quotedPrice:     sell.finalPrice!,
          });
          orderId = res.order._id;
        } catch (e: any) {
          console.warn("Backend order submission failed:", e.message);
          throw e;
        }
      }

      sellStore.setPickup(form);
      const refNo = "SG" + Math.floor(100000 + Math.random() * 900000);
      sellStore.setReference(refNo, orderId);
      nav("/confirmation");
    } catch (e: any) {
      const model = sell.product;
      submitLead({
        phone: sell.phone,
        name: form.name,
        deviceName: model?.name || "",
        brandId: sell.brandId,
        productId: sell.productId,
        algoId: sell.algoId,
        basePrice: sell.algoBasePrice || model?.basePrice,
        quotedPrice: sell.finalPrice,
        answers: sell.answers,
        pickup: { date: form.date, timeSlot: form.timeSlot, address: [form.address, form.landmark].filter(Boolean).join(", "), city: form.city, pincode: form.pincode },
        step: "scheduled",
      }).catch(() => {});
      setApiError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSub(false);
    }
  };

  const selSlot     = TIME_SLOTS.find(s => s.value === form.timeSlot);
  const selDateNice = formatNice(form.date ?? "");

  return (
    <div className="page-bg">
      <Header />
      <div ref={topRef} style={{ maxWidth: 660, margin: "0 auto", padding: "30px 16px 80px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 14 }}>
          <div>
            <button type="button" onClick={() => nav("/price")}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--text2)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-body)", marginBottom: 10, transition: "color 0.2s" }}
              onMouseEnter={e => (e.currentTarget.style.color = "var(--brand)")}
              onMouseLeave={e => (e.currentTarget.style.color = "var(--text2)")}>
              ← Back to offer
            </button>
            <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(22px,4vw,28px)", color: "var(--text)", letterSpacing: "-0.5px" }}>Schedule Pickup</h1>
            <p style={{ fontSize: 14, color: "var(--text2)", marginTop: 4 }}>Free doorstep service — pick your preferred date & time.</p>
          </div>
          <div style={{
            background: "linear-gradient(135deg, var(--brand), var(--brand-dark))",
            borderRadius: 16, padding: "14px 22px", textAlign: "center", flexShrink: 0,
            boxShadow: "0 6px 20px rgba(255,107,53,0.3)",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Your Payout</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 24, color: "#fff", lineHeight: 1.2, marginTop: 2 }}>₹{sell.finalPrice?.toLocaleString("en-IN")}</div>
          </div>
        </div>

        <StepIndicator current={step} />

        {apiError && (
          <div style={{
            background: "linear-gradient(135deg, #fef2f2, #fee2e2)", border: "1.5px solid #fca5a5",
            borderRadius: 14, padding: "14px 18px", marginBottom: 18, fontSize: 13, color: "#dc2626",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            {apiError}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* ── STEP 0: Date & Time ── */}
          {step === 0 && (
            <>
              <Card>
                <SectionHead icon="📅" bg="var(--indigo-light)" title="Choose Pickup Date" sub="Select any available day in the next 30 days" />
                <Calendar selected={form.date ?? ""} onChange={ymd => setF("date", ymd)} />
                {errors.date && <p style={{ fontSize: 12, color: "var(--danger)", marginTop: 12, display: "flex", alignItems: "center", gap: 4 }}>⚠ {errors.date}</p>}
              </Card>
              <Card>
                <SectionHead icon="⏰" bg="var(--brand-light)" title="Choose Time Slot" sub="Pick a 2-hour window that works for you" />
                <TimeSlotPicker selected={form.timeSlot ?? ""} onChange={v => setF("timeSlot", v)} error={errors.timeSlot} />
                {form.date && form.timeSlot && (
                  <div style={{
                    marginTop: 18, background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                    border: "1.5px solid #86efac", borderRadius: 14, padding: "14px 18px",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>✓</div>
                    <div>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, color: "#16a34a" }}>Slot confirmed!</div>
                      <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 2 }}><strong style={{ color: "var(--text)" }}>{selDateNice}</strong> · {selSlot?.label}</div>
                    </div>
                  </div>
                )}
              </Card>
              <NavButtons onBack={() => nav("/price")} onNext={goNext} nextLabel="Continue → Your Details" />
            </>
          )}

          {/* ── STEP 1: Personal Details ── */}
          {step === 1 && (
            <>
              <Card>
                <SectionHead icon="👤" bg="var(--indigo-light)" title="Your Details" sub="So our technician knows who to meet" />
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <FormField label="Full Name *" error={errors.name}>
                    <TextInput value={form.name ?? ""} onChange={v => setF("name", v)} placeholder="e.g. Rahul Sharma" error={errors.name} />
                  </FormField>
                  <div>
                    <label style={labelSt}>Mobile Number</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--bg)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "13px 16px" }}>
                      <span style={{ fontSize: 14, color: "var(--text3)" }}>+91</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{sell.phone}</span>
                      <span style={{ marginLeft: "auto", background: "linear-gradient(135deg, #dcfce7, #bbf7d0)", color: "#16a34a", fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "3px 10px", display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#16a34a" }} /> Verified
                      </span>
                    </div>
                  </div>
                </div>
                {/* Selected slot summary */}
                <div style={{ marginTop: 20, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text)" }}>
                    <span style={{ fontSize: 14 }}>📅</span> {selDateNice}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text)" }}>
                    <span style={{ fontSize: 14 }}>⏰</span> {selSlot?.label}
                  </div>
                  <button type="button" onClick={() => setStep(0)} style={{ marginLeft: "auto", fontSize: 12, color: "var(--brand)", background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontFamily: "var(--font-body)", transition: "opacity 0.2s" }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = "0.7")}
                    onMouseLeave={e => (e.currentTarget.style.opacity = "1")}>Edit</button>
                </div>
              </Card>
              <NavButtons onBack={goPrev} onNext={goNext} nextLabel="Continue → Address" />
            </>
          )}

          {/* ── STEP 2: Address ── */}
          {step === 2 && (
            <>
              <Card>
                <SectionHead icon="📍" bg="#fef2f2" title="Pickup Address" sub="Where should our technician come?" />
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <FormField label="House / Flat / Building, Street *" error={errors.address}>
                    <TextareaInput value={form.address ?? ""} onChange={v => setF("address", v)} placeholder="e.g. Flat 4B, Sunrise Apartments, MG Road" error={errors.address} />
                  </FormField>
                  <FormField label="Landmark (optional)">
                    <TextInput value={form.landmark ?? ""} onChange={v => setF("landmark", v)} placeholder="Near Metro Station, Opposite City Mall…" />
                  </FormField>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))", gap: 16 }}>
                    <FormField label="City *" error={errors.city}>
                      <CitySelect value={form.city ?? ""} onChange={v => setF("city", v)} error={errors.city} />
                    </FormField>
                    <FormField label="Pincode *" error={errors.pincode}>
                      <TextInput value={form.pincode ?? ""} onChange={v => setF("pincode", v.replace(/\D/g, "").slice(0, 6))} placeholder="110001" mode="numeric" maxLength={6} error={errors.pincode} />
                    </FormField>
                  </div>
                </div>
              </Card>

              {/* Summary card */}
              {form.address && form.city && (
                <div style={{
                  background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                  border: "1.5px solid #86efac", borderRadius: 20, padding: "22px 22px",
                }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: "#16a34a", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>✅</span> All details ready!
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 120px), 1fr))", gap: 10 }}>
                    {[
                      { icon: "📅", label: "Date", val: selDateNice, color: "var(--indigo)" },
                      { icon: "⏰", label: "Time", val: selSlot?.label, color: "var(--brand)" },
                      { icon: "👤", label: "Name", val: form.name, color: "var(--indigo)" },
                      { icon: "📍", label: "City", val: form.city, color: "#dc2626" },
                      { icon: "💰", label: "Payout", val: `₹${sell.finalPrice?.toLocaleString("en-IN")}`, color: "#16a34a" },
                    ].map((r, i) => (
                      <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "11px 14px", boxShadow: "var(--shadow-sm)", border: "1px solid rgba(0,0,0,0.04)" }}>
                        <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{r.icon} {r.label}</div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{r.val || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <NavButtons onBack={goPrev} onNext={handleSubmit} nextLabel="Confirm Pickup →" isSubmit submitting={submitting} />
              <p style={{ textAlign: "center", fontSize: 12, color: "var(--text3)", marginTop: 4 }}>🔒 Free cancellation up to 2 hours before pickup · No charges apply</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
